import { TOOLBAR_DROPDOWN_PT, TOOLBAR_LABEL_CLASSES } from '@/components/projects/toolbarDropdown';
import type { ProjectListItem } from '@/types/project.types';
import { formatUserName } from '@/utils/formatters';
import { Dropdown } from 'primereact/dropdown';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

interface FilterControlsProps {
  projects: ProjectListItem[];
  selectedOwnerId: string | null;
  onOwnerFilterChange: (ownerId: string | null) => void;
}

const ALL_USERS_VALUE = '__ALL__';

/** "Filter by user" for the projects toolbar; the option list is built from the loaded page. */
export const FilterControls: FC<FilterControlsProps> = ({ projects, selectedOwnerId, onOwnerFilterChange }) => {
  const { t } = useTranslation();
  // Get unique owners with their names
  const ownerMap = new Map<string, ProjectListItem>();
  projects.forEach((p) => {
    if (p.owner_id && !ownerMap.has(p.owner_id)) {
      ownerMap.set(p.owner_id, p);
    }
  });

  const uniqueOwners = Array.from(ownerMap.entries())
    .map(([ownerId, project]) => ({
      ownerId,
      name: formatUserName(project),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const ownerOptions = [
    { label: t('home.allUsers'), value: ALL_USERS_VALUE },
    ...uniqueOwners.map(({ ownerId, name }) => ({
      label: name,
      value: ownerId,
    })),
  ];

  // Convert null to ALL_USERS_VALUE for dropdown
  const dropdownValue = selectedOwnerId === null ? ALL_USERS_VALUE : selectedOwnerId;

  return (
    <div className="flex items-center gap-2">
      <span className={TOOLBAR_LABEL_CLASSES}>{t('home.filterByUser')}</span>
      <Dropdown
        value={dropdownValue}
        options={ownerOptions}
        onChange={(e) => {
          // Convert ALL_USERS_VALUE back to null
          const value = e.value === ALL_USERS_VALUE || e.value === undefined || e.value === null ? null : e.value;
          onOwnerFilterChange(value);
        }}
        className="w-40"
        placeholder={t('home.selectUser')}
        optionLabel="label"
        optionValue="value"
        pt={TOOLBAR_DROPDOWN_PT}
        aria-label={t('home.filterByUser')}
      />
    </div>
  );
};
